import React, { useState } from "react";
import PropTypes from "prop-types";
import ReactGA from "react-ga4";
import { useQuery } from "@apollo/client";
import { BookQuery } from "arena/queries";
import BookHelmet from "./BookHelmet";
import ErrorScreen from "core/components/ErrorScreen";
import NoMatchScreen from "arena/components/NoMatchScreen";
import { Navigate } from "react-router-dom";
import Container from "@mui/material/Container";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Grid from "@mui/material/Grid";
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ThumbnailCardMedia from "arena/components/ThumbnailCardMedia";
import TagList from "arena/components/TagList";
import { CloseRouteDialogButton } from "arena/components/RouteDialog";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import VisibilityIcon from "@mui/icons-material/Visibility";
import useProductDialog from "arena/hooks/useProductDialog";
import ProductDialog from "arena/components/ProductDialog";
import BookTableOfContentsDialog from "./BookTableOfContentsDialog";
import useIsMobile from "arena/hooks/useIsMobile";
import PaymentButtons from "core/components/PaymentButtons";
import { useArena } from "core/components/ArenaProvider";
import PaymentsIcon from "@mui/icons-material/Payments";
import { isTelegramMiniApp } from "core/utils/telegram";
import ShareButton from "arena/components/ShareButton";

// Компонент для вертикального превью книги (пропорции 2:3)
const StyledThumbnailCardMedia = styled(ThumbnailCardMedia)(({ theme }) => ({
  width: "100%",
  position: "relative",
  height: 0,
  paddingTop: "150%", // 2:3 пропорция (3/2 = 1.5 = 150%)
  backgroundColor: theme.palette.mode === "dark" ? "black" : "rgba(0,0,0,0.01)",
}));

export default function Book({ params: { contentId } }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation("arena");
  const { state, dispatch } = useProductDialog({});
  const isMobile = useIsMobile();
  const [tocDialogOpen, setTocDialogOpen] = useState(false);
  const arena = useArena();

  const onCompleted = (data) => {
    if (data.content) {
      ReactGA.send({
        hitType: "pageview",
        page: location.pathname,
        title: data.content.title,
      });
    }
  };

  const { data, loading, refetch, error } = useQuery(BookQuery, {
    fetchPolicy: "cache-and-network",
    variables: {
      id: contentId,
    },
    onCompleted,
  });

  const content = data?.content;

  if (error) {
    return <ErrorScreen error={error} />;
  }

  if (!content && !loading) {
    return <NoMatchScreen />;
  }

  if (content && content.type !== "BOOK") {
    return (
      <Navigate
        to={content.siteUrl.replace("book", content.type.toLowerCase())}
      />
    );
  }

  return (
    <>
      {content && <BookHelmet content={content} />}

      {content && (
        <Container maxWidth="md" sx={{ p: 0 }}>
          <Card
            sx={{
              backgroundColor: "rgba(255,255,255,0.03)",
              position: "relative",
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "flex-start", p: 2, pb: 1 }}
            >
              <CloseRouteDialogButton />
              <CardHeader
                title={content.title}
                titleTypographyProps={{
                  sx: {
                    mb: 2,
                    ml: isTelegramMiniApp ? 0 : 7,
                  },
                }}
                sx={{ flex: 1, p: 0 }}
              />
            </Box>

            <CardContent sx={{ pt: 0 }}>
              <Grid container spacing={2}>
                {/* Левая часть - картинка */}
                <Grid item xs={12} md={3}>
                  {content.verticalThumbnails?.length > 0 ||
                  content.thumbnails?.length > 0 ? (
                    <StyledThumbnailCardMedia
                      thumbnails={
                        content.verticalThumbnails?.length > 0
                          ? content.verticalThumbnails
                          : content.thumbnails
                      }
                      alt={content.title}
                      size="MAXRES"
                    />
                  ) : (
                    <Box
                      sx={{
                        width: "100%",
                        height: 0,
                        paddingTop: "150%",
                        backgroundColor: "rgba(0,0,0,0.1)",
                        borderRadius: 1,
                        position: "relative",
                      }}
                    />
                  )}
                </Grid>

                {/* Правая часть - описание и контролы */}
                <Grid item xs={12} md={9}>
                  {content.tags.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <TagList tags={content.tags} />
                    </Box>
                  )}

                  <Box
                    sx={{ mb: 2, "& p:first-of-type": { marginTop: 0 } }}
                    dangerouslySetInnerHTML={{
                      __html: content.premiumDescription || content.description,
                    }}
                  />

                  {/* Прогресс чтения */}
                  {content.bookMetadata?.userBook && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {t("book.readingProgress", "Прогресс чтения")}:{" "}
                        {Math.round(
                          content.bookMetadata.userBook.progressPercent
                        )}
                        %
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={content.bookMetadata.userBook.progressPercent}
                        sx={{ height: 8, borderRadius: 1 }}
                      />
                    </Box>
                  )}

                  {/* Кнопки управления */}
                  <Grid
                    container
                    spacing={2}
                    sx={{ mb: 2 }}
                    justifyContent={isMobile ? "center" : "flex-end"}
                    alignItems="center"
                  >
                    {arena?.isShareEnabled && (
                      <Grid item xs={isMobile ? 12 : "auto"}>
                        <ShareButton
                          fullWidth={isMobile}
                          shareData={{
                            title: content.title,
                            url: content.shareUrl,
                          }}
                        />
                      </Grid>
                    )}
                    {content.bookMetadata?.tableOfContents &&
                      content.bookMetadata.tableOfContents.length > 0 && (
                        <Grid item xs={isMobile ? 12 : "auto"}>
                          <Button
                            variant="contained"
                            color="primary"
                            fullWidth={isMobile}
                            startIcon={<MenuBookIcon />}
                            onClick={() => setTocDialogOpen(true)}
                          >
                            {t("book.viewTableOfContents", "Оглавление")}
                          </Button>
                        </Grid>
                      )}
                    <Grid item xs={isMobile ? 12 : "auto"}>
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth={isMobile}
                        disabled={!content.access?.token}
                        onClick={() => {
                          if (content.access?.token) {
                            navigate(`/book/${contentId}/read`);
                          }
                        }}
                        startIcon={<VisibilityIcon />}
                      >
                        {content.bookMetadata?.userBook
                          ? t("book.continueReading", "Продолжить")
                          : t("book.read", "Читать")}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Информация о платном контенте */}
                {content.products?.nodes?.length > 0 && !content.access?.token && (
                  <Grid item xs={12}>
                    <Box sx={{ mt: 2, p: 2 }}>
                      <ErrorScreen
                        title={t(
                          "paidContent.title",
                          "Доступ к контенту ограничен"
                        )}
                        text={t(
                          "paidContent.description",
                          "Доступ к контенту платный"
                        )}
                        icon={PaymentsIcon}
                        button={
                          <PaymentButtons
                            products={content.products.nodes.filter(
                              (product) => product.prices.length > 0
                            )}
                            isTelegramArena={arena?.isTelegramArena}
                            onClickProductButton={dispatch}
                          />
                        }
                      />
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
          {content.products && (
            <ProductDialog
              products={content.products.nodes}
              state={state}
              dispatch={dispatch}
              refetch={refetch}
            />
          )}
          <BookTableOfContentsDialog
            open={tocDialogOpen}
            onClose={() => setTocDialogOpen(false)}
            tableOfContents={content.bookMetadata?.tableOfContents}
            bookTitle={content.title}
            contentId={contentId}
            hasAccess={!!content.access?.token}
          />
        </Container>
      )}
    </>
  );
}

Book.propTypes = {
  params: PropTypes.shape({
    contentId: PropTypes.string.isRequired,
  }),
};
